import { useTranslation } from 'react-i18next';
import { isExpiredDate, daysUntil } from '../../utils/expiry';

interface ExpiryCellProps {
  expiresAt?: string | null;
  /** Optional server-derived flag; falls back to a client-side computation. */
  isExpired?: boolean;
  className?: string;
}

/**
 * Read-only badge summarising a per-student access expiry:
 *   no date => "No expiry" (gray), expired => "Expired" (red),
 *   expiring within 14 days => amber, otherwise => green "Until <date>".
 * Shared by course-enrollment and exam-access management tables.
 */
export default function ExpiryCell({ expiresAt, isExpired, className = '' }: ExpiryCellProps) {
  const { t } = useTranslation('common');

  const expired = isExpired ?? isExpiredDate(expiresAt);

  if (!expiresAt) {
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 ${className}`}>
        {t('expiry.noExpiry')}
      </span>
    );
  }

  if (expired) {
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 ${className}`}>
        {t('expiry.expiredOn', { date: expiresAt })}
      </span>
    );
  }

  const remaining = daysUntil(expiresAt);
  const soon = remaining !== null && remaining <= 14;
  const tone = soon ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';

  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tone} ${className}`} title={expiresAt}>
      {t('expiry.untilDate', { date: expiresAt })}
      {soon && remaining !== null ? ` · ${t('expiry.daysLeft', { days: remaining })}` : ''}
    </span>
  );
}
